import { useState } from 'react'

import styled from '@emotion/styled'
import { Select } from 'antd'
import { RadioChangeEvent } from 'antd/lib/radio'
import isEmpty from 'lodash/isEmpty'

import { COLORS, NOTIFICATIONS } from 'shared/config/constants'
import { useUploadDocument } from 'shared/hooks/api'
import useModal from 'shared/hooks/useModal'
import type { DocumentInApplication } from 'shared/interfaces/api/document'
import { isManagerApp, getCachedActiveCompanySlug } from 'shared/utils/auth'
import { useVeroFlag } from 'shared/utils/featureFlags'
import { openNotification } from 'shared/utils/ui'
import { VeroFlag } from 'shared/utils/veroFlags'

import FlexContainer from '../FlexContainer'
import Icon from '../Icon'
import { Modal as BaseModal } from '../Modal/Modal'
import Spacer from '../Spacer'
import { RegularText, SmallText, SmallTitle } from '../Typography'

import {
  DocumentName,
  DocumentType,
  DocumentTypeOptions,
  Modal,
  SuccessMessageContainer,
} from './styled'

interface DocumentsUploadModalProps {
  acceptedFiles: File[]
  applicationId: number
  applicantVrn: string
  personVrn: string
  requiredDocuments: DocumentInApplication[]
  propertyName?: string
}

const internalDocument: DocumentInApplication = {
  attachmentRequestId: 0,
  documents: [],
  formType: 'internal-document',
  isRequired: false,
  name: 'Internal Document',
  ocr: false,
  requiredQty: 0,
  skipped: false,
}

const INTERNATIONAL_DOCUMENT_NAME = 'International Document'

const internationalDocument: DocumentInApplication = {
  attachmentRequestId: 0,
  documents: [],
  formType: 'other',
  isRequired: false,
  name: INTERNATIONAL_DOCUMENT_NAME,
  ocr: true,
  requiredQty: 0,
  skipped: false,
}

const BASE_COUNTRY_OPTIONS = [
  'Australia',
  'Canada',
  'China',
  'Guatemala',
  'India',
  'Mexico',
  'Philippines',
  'South Korea',
  'Turkey',
  'United Kingdom',
]

// PRODUCT-1949: only shown to companies with the use-intl-ocr-fixes flag enabled.
const INTL_OCR_FIXES_COUNTRY_OPTIONS = [
  'Brazil',
  'Colombia',
  'Egypt',
  'Israel',
  'Jordan',
  'Kuwait',
  'Portugal',
  'Saudi Arabia',
  'Ukraine',
  'United Arab Emirates',
]

const INTERNATIONAL_DOCUMENT_TYPES = [
  'Paystub',
  'Tax Form',
  'Employment Letter',
  'Bank Statement',
  'Other',
]

const BetaBadge = styled.span`
  background-color: ${COLORS.violetTheme.lightViolet};
  color: #ffffff;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
  padding: 2px 6px;
  border-radius: 4px;
  margin-left: 8px;
  text-transform: uppercase;
`

const InternationalFields = styled.div`
  width: 100%;
  padding: 12px 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const FieldLabel = styled(SmallText)`
  font-weight: 600;
  margin-bottom: 4px;
`

export const DocumentsUploadModal = ({
  acceptedFiles,
  applicationId,
  applicantVrn,
  personVrn,
  requiredDocuments,
  propertyName,
}: DocumentsUploadModalProps) => {
  const {
    selfApi: { closeDialog },
  } = useModal()
  const [animate, setAnimate] = useState(false)
  const [selectedFormTypeName, setSelectedFormTypeName] = useState<
    string | null
  >(null)
  const [processedDocumentIndex, setProcessedDocumentIndex] = useState(0)
  const { uploadDocument, isLoading } = useUploadDocument(applicationId)
  const showInternationalUpload = useVeroFlag(VeroFlag.useInternationalDocumentUpload)
  const isIntlOcrFixesEnabled = useVeroFlag(VeroFlag.useIntlOcrFixes)
  const countryOptions = isIntlOcrFixesEnabled
    ? [...BASE_COUNTRY_OPTIONS, ...INTL_OCR_FIXES_COUNTRY_OPTIONS].sort()
    : BASE_COUNTRY_OPTIONS

  const [selectedCountry, setSelectedCountry] = useState<string | null>(null)
  const [selectedDocumentType, setSelectedDocumentType] = useState<
    string | null
  >(null)

  const isInternationalSelected =
    selectedFormTypeName === INTERNATIONAL_DOCUMENT_NAME

  const updatedRequirdDocuments = [
    ...requiredDocuments,
    ...(isManagerApp() && showInternationalUpload ? [internationalDocument] : []),
    ...(isManagerApp() ? [internalDocument] : []),
  ]

  const onRadioCheck = ({ target: { value } }: RadioChangeEvent) => {
    setSelectedFormTypeName(value)
    if (value !== INTERNATIONAL_DOCUMENT_NAME) {
      setSelectedCountry(null)
      setSelectedDocumentType(null)
    }
  }

  const isSubmitDisabled = !selectedFormTypeName || (isInternationalSelected && (!selectedCountry || !selectedDocumentType))

  return (
    <Modal
      closable={!animate}
      closeOnSubmit={false}
      showCancelLink={false}
      showSubmitButton={!animate}
      submitButtonDisabled={isSubmitDisabled}
      submit={() => {
        const isInternalDocument = selectedFormTypeName === 'Internal Document'
        const isInternational = selectedFormTypeName === INTERNATIONAL_DOCUMENT_NAME
        if (!isInternalDocument && !isInternational && isEmpty(personVrn)) {
          openNotification(
            'Applicant has not yet started their application',
            NOTIFICATIONS.error,
          )
          return
        }
        const file = acceptedFiles[processedDocumentIndex]
        setAnimate(true)

        const description = isInternational
          ? `${selectedDocumentType} (${selectedCountry})`
          : selectedFormTypeName

        uploadDocument({
          description,
          file,
          filename: file.name,
          ocr: !!updatedRequirdDocuments.find(
            ({ name }) => name === selectedFormTypeName,
          )?.ocr,
          owner: isInternalDocument
            ? `vrn:applicant::${applicantVrn}`
            : `vrn:person::${personVrn}`,
          ownerType: isInternalDocument ? 'applicant' : 'person',
          companySlug: getCachedActiveCompanySlug(),
          propertyName,
          documentIntent: isInternational ? 'international' : undefined,
        })
        setTimeout(() => {
          if (processedDocumentIndex + 1 === acceptedFiles.length) {
            closeDialog()
          } else {
            setAnimate(false)
            setSelectedFormTypeName(null)
            setSelectedCountry(null)
            setSelectedDocumentType(null)
            setProcessedDocumentIndex((prev) => prev + 1)
          }
        }, 1500)
      }}
      submitButtonLabel="UPLOAD"
      submitting={isLoading}
      style={{ maxWidth: 423 }}
      width="auto"
    >
      <BaseModal.Body>
        <FlexContainer alignItems="center" flexDirection="column">
          <Icon.Document height={120} width={96} />
          <DocumentName>
            {acceptedFiles[processedDocumentIndex].name}
          </DocumentName>
          <SmallText>
            Upload {processedDocumentIndex + 1} of {acceptedFiles.length}
          </SmallText>
          <Spacer size={Spacer.SIZES.default} />
          <SmallTitle strong>What kind of file is this?</SmallTitle>
          <Spacer size={Spacer.SIZES.default} />
          <DocumentTypeOptions
            animate={animate}
            onChange={onRadioCheck}
            value={selectedFormTypeName}
            selectedOptionIndex={updatedRequirdDocuments.findIndex(
              ({ name }) => selectedFormTypeName === name,
            )}
            touched={!!selectedFormTypeName}
          >
            {updatedRequirdDocuments.map(({ formType, name }, index) => (
              <DocumentType
                key={`${formType}_${index.toString()}`}
                value={name}
              >
                <RegularText>
                  {name}
                  {name === INTERNATIONAL_DOCUMENT_NAME && (
                    <BetaBadge>Beta</BetaBadge>
                  )}
                </RegularText>
              </DocumentType>
            ))}
            <SuccessMessageContainer
              alignItems="center"
              className="success-message-container"
              flexDirection="column"
              gap="16px"
              numberOfOptions={updatedRequirdDocuments.length}
            >
              <Icon.SuccessIcon fill={COLORS.green} height={168} width={168} />
              <SmallTitle>Success!</SmallTitle>
            </SuccessMessageContainer>
          </DocumentTypeOptions>
          {isInternationalSelected && !animate && (
            <InternationalFields>
              <SmallText>
                Select the country and document type for this international
                income document.
              </SmallText>
              <div>
                <FieldLabel>Country</FieldLabel>
                <Select
                  placeholder="Select a country..."
                  value={selectedCountry}
                  onChange={(value) => setSelectedCountry(value)}
                  style={{ width: '100%' }}
                >
                  {countryOptions.map((country) => (
                    <Select.Option key={country} value={country}>
                      {country}
                    </Select.Option>
                  ))}
                </Select>
              </div>
              <div>
                <FieldLabel>Document type</FieldLabel>
                <Select
                  placeholder="Select document type..."
                  value={selectedDocumentType}
                  onChange={(value) => setSelectedDocumentType(value)}
                  style={{ width: '100%' }}
                >
                  {INTERNATIONAL_DOCUMENT_TYPES.map((type) => (
                    <Select.Option key={type} value={type}>
                      {type}
                    </Select.Option>
                  ))}
                </Select>
              </div>
            </InternationalFields>
          )}
        </FlexContainer>
      </BaseModal.Body>
    </Modal>
  )
}
